import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import { cn } from "@RHCommerceDev/utils/cn";
import { groupChildCategories } from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { useEnv } from "hooks/useEnv";
import React, { useState } from "react";
import { Typography } from "unstyled-ui-components";
import memoize from "@RHCommerceDev/utils/memoize";
import parseUrlPath from "@RHCommerceDev/utils/parse-url-path";

const NavigationDropdown = ({
  selectedCategory
}: {
  selectedCategory: NavigationElement | undefined;
}) => {
  const locale = useLocalization();
  const { FEATURE_SHOP_UI } = useEnv();

  const [selectedSubcategoryIndex, setSelectedSubcategoryIndex] =
    useState<number>(-1);
  const selectedSubcategory =
    selectedCategory?.childCategories?.[selectedSubcategoryIndex];

  const groupedCategories = groupChildCategories(
    selectedSubcategory?.childCategories
  );

  if (!selectedCategory) {
    return null;
  }

  return (
    <>
      <div className="flex h-screen gap-20 bg-rh-mocha pb-10 pt-5 text-white">
        {/* SUBCATEGORY COLUMN */}
        <div className="flex flex-col items-start gap-5 overflow-auto pr-3">
          {selectedCategory?.childCategories?.map((primaryItem, primaryIndex) =>
            primaryItem?.childCategories === null ? (
              <a
                key={primaryIndex}
                href={
                  FEATURE_SHOP_UI
                    ? parseUrlPath(`${locale}${primaryItem?.targetUrl}`)
                    : `${locale}${primaryItem?.targetUrl}`
                }
                className="text-inherit no-underline"
              >
                <Typography
                  variant="default"
                  fontFamily={
                    selectedSubcategoryIndex === primaryIndex
                      ? "rhcRoman"
                      : "rhc"
                  }
                  fontWeight="normal"
                  className={cn(
                    "text-base uppercase leading-[1.5em] tracking-[0.05em]"
                  )}
                >
                  {primaryItem?.displayName}
                </Typography>
              </a>
            ) : (
              <button
                key={primaryIndex}
                onClick={() => setSelectedSubcategoryIndex(primaryIndex)}
                className="p-0 text-inherit"
              >
                <Typography
                  variant="default"
                  fontFamily={
                    selectedSubcategoryIndex === primaryIndex
                      ? "rhcRoman"
                      : "rhc"
                  }
                  fontWeight="normal"
                  className={cn(
                    "text-base uppercase leading-[1.5em] tracking-[0.05em]",
                    {
                      "font-normal": selectedSubcategoryIndex === primaryIndex
                    }
                  )}
                >
                  {primaryItem?.displayName}
                </Typography>
              </button>
            )
          )}
        </div>

        {/* THIRD COLUMN */}
        {selectedSubcategoryIndex !== -1 && (
          <div className="columns-2 gap-x-20 text-white [column-fill:_auto]">
            {groupedCategories?.map((group, groundIndex) => (
              <div
                key={groundIndex}
                className="mb-4 flex break-inside-avoid flex-col gap-2.5"
              >
                {group.header && (
                  <Typography
                    variant="default"
                    fontFamily="rhcRoman"
                    fontWeight="normal"
                    className="text-base uppercase leading-[1.5em] tracking-[0.05em] text-inherit"
                  >
                    {group.header?.displayName}
                  </Typography>
                )}

                {group.children.map((tertiaryItem, tertiaryIndex) => (
                  <a
                    key={tertiaryIndex}
                    href={
                      FEATURE_SHOP_UI
                        ? parseUrlPath(`${locale}${tertiaryItem?.targetUrl}`)
                        : `${locale}${tertiaryItem?.targetUrl}`
                    }
                    className="text-inherit no-underline"
                  >
                    <Typography
                      variant="default"
                      fontFamily="rhc"
                      fontWeight="normal"
                      className="text-base capitalize leading-[1.5em] tracking-[0.05em]"
                    >
                      {tertiaryItem?.displayName}
                    </Typography>
                  </a>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default memoize(NavigationDropdown);
